import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkFunnelUiVisible,
  checkFunnelUiWritable,
  FunnelInductionStatus,
} from "@repo/app-types/entities/funnel";
import {
  asTeammateId,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import type { FC } from "react";
import { Navigate, useParams } from "react-router";
import {
  useFunnelQuery,
  useVoiceAgentFunnelsQuery,
} from "../../../api/entities/funnel.js";
import { appPaths } from "../../../app/appPaths.js";
import { PageBody } from "../../../components/layout/pageLayout.js";
import { useFeatureFlagVariant } from "../../../helpers/featureFlags/hooks.js";
import { FunnelCreateForm } from "./funnelCreateForm.js";
import {
  FunnelInductionFailedBanner,
  FunnelInductionPending,
} from "./funnelInductionStates.js";
import { FunnelReadyView } from "./funnelReadyView.js";

/**
 * The Funnel view of the voice agent's Performance tab, behind
 * `FeatureFlagKey.VoiceAgentFunnelUi`.
 */
export const TeammateAgentFunnelTab: FC = () => {
  const { teammateAgentId: teammateAgentIdParam } = useParams();
  if (!teammateAgentIdParam) {
    throw new Error("Missing teammateAgentId param.");
  }
  const teammateAgentId = asTeammateId(teammateAgentIdParam);

  const funnelUiVariant = useFeatureFlagVariant(
    FeatureFlagKey.VoiceAgentFunnelUi,
  );
  if (!checkFunnelUiVisible(funnelUiVariant)) {
    return (
      <Navigate
        to={appPaths.teammateAgentOverview.build({ teammateAgentId })}
        replace
      />
    );
  }

  return (
    <PageBody width={PageWidth.Wide}>
      {/* Keyed so form drafts start fresh for each agent. */}
      <TeammateAgentFunnel
        key={teammateAgentId}
        teammateAgentId={teammateAgentId}
        isWritable={checkFunnelUiWritable(funnelUiVariant)}
      />
    </PageBody>
  );
};

interface TeammateAgentFunnelProps {
  teammateAgentId: TeammateId;
  /** Whether the funnel can be created and edited here. */
  isWritable: boolean;
}

const TeammateAgentFunnel: FC<TeammateAgentFunnelProps> = (props) => {
  const { teammateAgentId, isWritable } = props;

  const funnelsQuery = useVoiceAgentFunnelsQuery(teammateAgentId);
  // In v1 an agent has at most one funnel.
  const funnelId = funnelsQuery.data?.[0]?.id;
  const funnelQuery = useFunnelQuery(funnelId ?? skipToken);
  const funnel = funnelQuery.data;

  if (funnelsQuery.isLoading) {
    return <DefaultSkeleton />;
  }
  if (funnelsQuery.isError || !funnelsQuery.data) {
    return (
      <p className="text-sm text-red-700">
        We couldn&apos;t load this agent&apos;s funnel. Refresh to try again.
      </p>
    );
  }
  if (!funnelId) {
    // Read-only has no tab without a funnel.
    return isWritable ? (
      <FunnelCreateForm subject={{ teammateAgentId }} />
    ) : (
      <Navigate
        to={appPaths.teammateAgentOverview.build({ teammateAgentId })}
        replace
      />
    );
  }
  if (funnelQuery.isLoading) {
    return <DefaultSkeleton />;
  }
  if (funnelQuery.isError || !funnel) {
    return (
      <p className="text-sm text-red-700">
        We couldn&apos;t load this funnel. Refresh to try again.
      </p>
    );
  }

  switch (funnel.induction_status) {
    case FunnelInductionStatus.Pending:
      return (
        <FunnelInductionPending
          funnelId={funnel.id}
          subjectDescription="this agent's recent interactions"
        />
      );
    case FunnelInductionStatus.Failed:
      return (
        <FunnelInductionFailedBanner
          funnelId={funnel.id}
          inductionError={funnel.induction_error}
          isWritable={isWritable}
        />
      );
    case FunnelInductionStatus.Complete:
      return (
        <FunnelReadyView funnel={funnel} teammateAgentId={teammateAgentId} />
      );
    default:
      return assertUnreachable(funnel.induction_status);
  }
};
